/**
 * El ticket de un cargo del banco, recién soltado: falta saber de qué inmueble es.
 *
 * Es lo ÚNICO que se pregunta, y ni siquiera hace falta contestarlo: un cargo de Iberdrola no
 * dice de qué piso es, pero su factura sí. Sin piso elegido, lo saca del nombre del fichero o de
 * la factura (su CUPS o su dirección); si nada lo dice, se avisa y se elige. El importe es el
 * del banco —es el dato, no una lectura— y el concepto lo propone la aplicación al leer el
 * ticket; se puede escribir si se prefiere otro.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BankMovement, LedgerProperty } from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Button } from '../../components/Button';
import { Drawer } from '../../components/overlay/Drawer';
import { Field, FormSection, Input, Select } from '../../components/form/Field';
import { toast } from '../../components/feedback/Toaster';
import { formatEurCents, formatShortDate } from '../../lib/format';
import styles from './Ledger.module.css';

interface MovementTicketDrawerProps {
  movement: BankMovement;
  file: File;
  properties: LedgerProperty[];
  /** El inmueble que se está mirando: casi siempre es ese. */
  defaultPropertyId?: string;
  onClose: () => void;
}

export function MovementTicketDrawer({ movement, file, properties, defaultPropertyId, onClose }: MovementTicketDrawerProps) {
  const queryClient = useQueryClient();
  const [propertyId, setPropertyId] = useState(defaultPropertyId ?? '');
  const [concepto, setConcepto] = useState('');

  const guardar = useMutation({
    mutationFn: () =>
      api.attachMovementTicket(movement.id, file, {
        ...(propertyId ? { propertyId } : {}),
        ...(concepto.trim() ? { concept: concepto.trim() } : {}),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      await queryClient.invalidateQueries({ queryKey: ['utilities'] });
      toast.success(
        propertyId
          ? 'Ticket guardado y gasto apuntado, ya enlazado a su cargo del banco.'
          : `Ticket guardado en ${result.property.name} y enlazado a su cargo del banco.`,
      );
      onClose();
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido guardar el ticket.'),
  });

  return (
    <Drawer
      open
      title="¿De qué inmueble es este gasto?"
      subtitle={`${formatShortDate(movement.bookedAt)} · ${formatEurCents(movement.amount)}`}
      onClose={onClose}
      footer={
        <div className={styles.pie}>
          <span />
          <div className={styles.pieDerecha}>
            <Button variant="secondary" size="sm" onClick={onClose} disabled={guardar.isPending}>
              Cancelar
            </Button>
            <Button size="sm" onClick={() => guardar.mutate()} disabled={guardar.isPending}>
              {guardar.isPending ? 'Leyendo el ticket…' : 'Guardar'}
            </Button>
          </div>
        </div>
      }
    >
      <FormSection title="En el extracto">
        <p className={styles.movementQuote}>{movement.description}</p>
        <p className={styles.aviso}>Ticket: {file.name}</p>
      </FormSection>
      <FormSection>
        <Field
          label="Inmueble"
          optional
          help="Si lo dejas sin elegir, lo saco del nombre del fichero o de la factura (su CUPS o su dirección)."
        >
          <Select
            options={properties.map((property) => ({ value: property.id, label: `${property.name} · ${property.city}` }))}
            placeholder="Que lo saque de la factura"
            value={propertyId}
            onChange={(event) => setPropertyId(event.target.value)}
          />
        </Field>
        <Field label="Concepto" optional help="Si lo dejas vacío, lo saco del ticket.">
          <Input value={concepto} onChange={(event) => setConcepto(event.target.value)} placeholder="Luz" maxLength={160} />
        </Field>
      </FormSection>
    </Drawer>
  );
}
